import { useState, useCallback, ReactNode, useEffect, forwardRef, useMemo } from 'react';

import {
  DataGrid,
  GridRow,
  GridRowProps,
  GridColDef,
  GridRowSelectionModel,
  GridColumnVisibilityModel,
  ptBR,
  GridPaginationModel,
  GridRowParams,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import { CustomToolbar } from './custom-toolbar';
import { CustomFooter } from './custom-footer';
import { EmptyState } from './custom-empty-state';
import { Radio, Stack } from '@mui/material';
import type { CheckboxProps } from '@mui/material/Checkbox';
import type { RadioProps } from '@mui/material/Radio';
import { m } from 'framer-motion';

type Props = {
  columns: GridColDef[];
  rows: any[];
  customFilters?: ReactNode;
  onFilterButtonClick?: () => void;
  totalRowsCount?: number;
  filteredRowsCount: number;
  sortModel: GridSortModel;
  paginationModel: GridPaginationModel;
  paginationMode?: 'client' | 'server';
  sortingMode?: 'client' | 'server';
  initialVisibleColumns: GridColumnVisibilityModel;
  searchTooltipMessage?: string;
  isLoading?: boolean;
  height?: number;
  checkboxSelection?: boolean;
  singleSelection?: boolean;
  disableColumnMenu?: boolean;
  selectionControlVariant?: 'checkbox' | 'radio';
  showQuickFilter?: boolean;
  rowSelectionModel?: GridRowSelectionModel;
  onRowClick?: (params: GridRowParams) => void;
  isRowSelectable?: (params: GridRowParams) => boolean;
  getRowClassName?: (params: GridRowParams) => string;
  setSortModel: (model: GridSortModel) => void;
  setPaginationModel: (model: GridPaginationModel) => void;
  excludeExportButton?: boolean;
  onRowSelectionModelChange?: (model: GridRowSelectionModel) => void;
  onHeaderSelectionCheckboxChange?: (checked: boolean) => void;
  onRowSelectionCheckboxChange?: (id: string, checked: boolean) => void;
  animatedEachRow?: boolean;
  pageSizeOptions?: number[];
};

const MotionGridRow = m.create(GridRow);

const RadioSelectionControl = forwardRef<HTMLButtonElement, CheckboxProps>(
  ({ indeterminate, size, ...props }, ref) => (
    <Radio
      ref={ref}
      {...(props as Omit<RadioProps, 'size'>)}
      size={size === 'large' ? 'medium' : size}
    />
  )
);

RadioSelectionControl.displayName = 'RadioSelectionControl';

const AnimatedGridRow = forwardRef<HTMLDivElement, GridRowProps>((props, ref) => {
  const delay = Math.min((props.index % 12) * 0.035, 0.38);

  return (
    <MotionGridRow
      ref={ref}
      {...props}
      initial={{ opacity: 0, filter: 'blur(3px)' }}
      animate={{ opacity: 1, filter: 'blur(0px)' }}
      transition={{
        duration: 0.28,
        delay,
        ease: [0.22, 1, 0.36, 1],
      }}
    />
  );
});

AnimatedGridRow.displayName = 'AnimatedGridRow';

export default function DataGridCustom({
  columns,
  rows,
  customFilters,
  onFilterButtonClick,
  totalRowsCount,
  filteredRowsCount,
  initialVisibleColumns,
  sortModel,
  paginationModel,
  paginationMode = 'server',
  sortingMode = 'server',
  searchTooltipMessage,
  isLoading,
  height,
  checkboxSelection = true,
  singleSelection = false,
  disableColumnMenu = false,
  selectionControlVariant = 'checkbox',
  showQuickFilter = true,
  rowSelectionModel,
  onRowClick,
  isRowSelectable,
  getRowClassName,
  setSortModel,
  setPaginationModel,
  excludeExportButton = false,
  onRowSelectionModelChange,
  onHeaderSelectionCheckboxChange,
  onRowSelectionCheckboxChange,
  animatedEachRow = false,
  pageSizeOptions = [10, 20, 30, 40, 50],
}: Props) {
  const apiRef = useGridApiRef();
  const isSingleSelection = singleSelection || selectionControlVariant === 'radio';

  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(initialVisibleColumns);

  const handleChangeColumnVisibilityModel = useCallback((model: GridColumnVisibilityModel) => {
    setColumnVisibilityModel(model);
  }, []);

  const getTogglableColumns = () => columns.map((column) => column.field);

  const slots = useMemo(
    () => ({
      toolbar: CustomToolbar,
      footer: CustomFooter,
      noRowsOverlay: EmptyState,
      ...(selectionControlVariant === 'radio' ? { baseCheckbox: RadioSelectionControl } : {}),
      ...(animatedEachRow ? { row: AnimatedGridRow } : {}),
    }),
    [animatedEachRow, selectionControlVariant]
  );

  useEffect(() => {
    const subscribeEvent = apiRef.current?.subscribeEvent;

    if (!subscribeEvent) {
      return undefined;
    }

    const unsubscribeHeader = subscribeEvent('headerSelectionCheckboxChange', (params: any) => {
      onHeaderSelectionCheckboxChange?.(params.value);
    });

    const unsubscribeRow = subscribeEvent('rowSelectionCheckboxChange', (params: any) => {
      onRowSelectionCheckboxChange?.(String(params.id), params.value);
    });
    return () => {
      unsubscribeHeader?.();
      unsubscribeRow?.();
    };
  }, [apiRef, onHeaderSelectionCheckboxChange, onRowSelectionCheckboxChange]);

  return (
    <Stack minHeight={height}>
      <DataGrid
        apiRef={apiRef}
        checkboxSelection={checkboxSelection}
        disableColumnMenu={disableColumnMenu}
        disableRowSelectionOnClick
        keepNonExistentRowsSelected
        loading={isLoading}
        rows={rows}
        rowCount={paginationMode === 'server' ? filteredRowsCount ?? 0 : undefined}
        rowSelectionModel={rowSelectionModel}
        columns={columns}
        paginationModel={paginationModel}
        onPaginationModelChange={(model) => {
          const page = model.pageSize !== paginationModel.pageSize ? 0 : model.page;
          setPaginationModel({ ...model, page });
        }}
        paginationMode={paginationMode}
        sortModel={sortModel}
        onSortModelChange={(model) => {
          setSortModel(model);
        }}
        sortingMode={sortingMode}
        pageSizeOptions={pageSizeOptions}
        onRowClick={onRowClick}
        isRowSelectable={isRowSelectable}
        getRowClassName={getRowClassName}
        onRowSelectionModelChange={(newSelectionModel) => {
          const nextSelectionModel =
            isSingleSelection && newSelectionModel.length > 1
              ? [newSelectionModel[newSelectionModel.length - 1]]
              : newSelectionModel;

          onRowSelectionModelChange?.(nextSelectionModel);
        }}
        columnVisibilityModel={columnVisibilityModel}
        onColumnVisibilityModelChange={handleChangeColumnVisibilityModel}
        slots={slots}
        slotProps={{
          toolbar: {
            showQuickFilter,
            quickFilterProps: {
              debounceMs: 500,
              placeholder: 'Buscar...',
            },
            searchTooltipMessage,
            onFilterButtonClick,
            excludeExportButton,
            children: customFilters,
          },
          footer: {
            totalRowsCount,
          } as any,
          columnsPanel: {
            getTogglableColumns,
          },
          basePopper: {
            placement: 'bottom-end',
          },
        }}
        localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
        sx={{
          height: '100%',
          '& .MuiDataGrid-virtualScroller': {
            minHeight: height || 500,
          },
          '& .MuiDataGrid-overlay': {
            minHeight: height || 500,
          },
          '& .MuiBadge-badge': {
            backgroundColor: 'primary.main',
            color: 'common.white',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid #e0e0e0',
            cursor: onRowClick ? 'pointer' : 'default',
          },
          '& .MuiDataGrid-row:hover': {
            cursor: onRowClick ? 'pointer' : 'default',
          },
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: '#E3E9EF',
            borderTopLeftRadius: '8px',
            borderTopRightRadius: '8px',
            overflow: 'hidden',
          },
          '& .MuiDataGrid-columnHeader:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-columnHeader.bold-header .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 'bold',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none',
          },
          ...(isSingleSelection
            ? {
                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-checkboxInput': {
                  visibility: 'hidden',
                },
              }
            : {}),
        }}
      />
    </Stack>
  );
}
